import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Avatar,
  IconButton,
  TextField,
  Button,
  Chip,
  Collapse,
  Snackbar,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  ChatBubbleOutlineOutlined,
  Send,
  Add,
  Close,
  People,
  AccessTime,
} from '@mui/icons-material';
import { getPosts, createPost, replyToPost, likePost } from '../services/api';
import { useNavigate } from 'react-router-dom';

// ── Daily Prompts ─────────────────────────────────────────────────────────────

const PROMPTS = [
  "What's one thing you're grateful for today? 🙏",
  "What's been your biggest challenge this week?",
  "Share one small win from today — no matter how small 🎉",
  "What does 'being strong' mean to you?",
  "What's one habit you're trying to build right now?",
  "How are you really doing today? Be honest 💙",
  "What's one thing that brought you peace this week?",
  "Share something that made you smile recently 😊",
  "What's one thing you wish someone had told you earlier?",
  "How do you recharge when life gets heavy?",
  "What's your go-to when you're feeling low?",
  "What's one boundary you've set that changed your life?",
  "Share a lesson you learned the hard way.",
  "What does your self-care routine look like?",
  "What's one fear you're working to overcome?",
  "How do you handle a bad day?",
  "What's something you're proud of that nobody knows about?",
  "What does mental health mean to you in one sentence?",
  "Who in your life makes you feel safe? Why?",
  "What's one thing you'd tell your younger self?",
  "How do you stay consistent when motivation is gone?",
  "What's a quote or verse that keeps you going?",
  "What does 'Imara' — being strong — look like in your daily life?",
  "What's one thing you're letting go of this week?",
  "Share something you've been carrying alone. You don't have to. 💙",
  "What does a good day look like for you?",
  "How do you celebrate your own progress?",
  "What's one relationship in your life you're grateful for?",
  "What are you looking forward to this week?",
  "What's one thing you need right now — and how can the community help?",
];

function getDailyPrompt() {
  const dayOfYear = Math.floor(
    (Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000
  );
  return PROMPTS[dayOfYear % PROMPTS.length];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

const AVATAR_COLORS = [
  '#4ade80', '#22d3ee', '#a78bfa', '#f472b6',
  '#fbbf24', '#fb923c', '#34d399', '#60a5fa',
];

function avatarColor(name = '') {
  let hash = 0;
  for (let c of name) hash = c.charCodeAt(0) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ── ReplyBox ──────────────────────────────────────────────────────────────────

function ReplyBox({ postId, onReplied }) {
  const [text, setText]       = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');

  async function submit() {
    if (!text.trim()) return;
    setLoading(true); setErr('');
    try {
      await replyToPost(postId, { content: text.trim() });
      setText('');
      onReplied();
    } catch {
      setErr('Could not send reply. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {err && <Typography sx={{ fontSize: '0.75rem', color: '#ef9a9a' }}>{err}</Typography>}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth size="small"
          placeholder="Write a reply…"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submit()}
          multiline maxRows={3}
          sx={{
            '& .MuiOutlinedInput-root': {
              background: 'rgba(255,255,255,0.05)',
              color: '#e8f4f8',
              fontSize: '0.85rem',
              borderRadius: 2,
              '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
              '&:hover fieldset': { borderColor: 'rgba(74,222,128,0.3)' },
              '&.Mui-focused fieldset': { borderColor: '#4ade80' },
            },
            '& .MuiInputBase-input::placeholder': { color: '#7fa8c8' },
          }}
        />
        <IconButton
          onClick={submit}
          disabled={!text.trim() || loading}
          sx={{
            background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
            color: '#0a1628',
            borderRadius: 2,
            flexShrink: 0,
            '&:hover': { opacity: 0.85 },
            '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: '#3a5a7a' },
          }}
        >
          {loading ? <CircularProgress size={16} sx={{ color: '#0a1628' }} /> : <Send fontSize="small" />}
        </IconButton>
      </Box>
    </Box>
  );
}

// ── PostCard ──────────────────────────────────────────────────────────────────

function PostCard({ post, onRefresh }) {
  const [showReplies, setShowReplies] = useState(false);
  const [liked,       setLiked]       = useState(post.liked ?? false);
  const [likeCount,   setLikeCount]   = useState(post.likes_count ?? post.likes ?? 0);
  const [liking,      setLiking]      = useState(false);

  const replies  = post.replies ?? [];
  const username = post.is_anonymous ? 'Anonymous' : (post.author ?? 'Anonymous');
  const color    = avatarColor(username);

  async function handleLike() {
    if (liking) return;
    setLiking(true);
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikeCount(c => wasLiked ? c - 1 : c + 1);
    try {
      await likePost(post.id);
    } catch {
      setLiked(wasLiked);
      setLikeCount(c => wasLiked ? c + 1 : c - 1);
    } finally {
      setLiking(false);
    }
  }

  return (
    <Box sx={{
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 3,
      p: { xs: 2, sm: 2.5 },
      transition: 'border-color 0.2s',
      '&:hover': { borderColor: 'rgba(74,222,128,0.2)' },
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Avatar sx={{
          width: 36, height: 36,
          background: `${color}22`,
          color,
          fontSize: '0.8rem',
          fontWeight: 700,
          border: `1px solid ${color}44`,
        }}>
          {getInitials(username)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#e8f4f8' }}>
            {username}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTime sx={{ fontSize: 11, color: '#7fa8c8' }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#7fa8c8' }}>
              {timeAgo(post.created_at ?? post.timestamp ?? post.date)}
            </Typography>
          </Box>
        </Box>
        {post.category && (
          <Chip label={post.category} size="small" sx={{
            background: 'rgba(74,222,128,0.1)',
            color: '#4ade80',
            fontSize: '0.65rem',
            height: 20,
            border: '1px solid rgba(74,222,128,0.2)',
          }} />
        )}
      </Box>

      {/* Content */}
      <Typography sx={{
        fontSize: '0.95rem',
        color: '#d4e8f0',
        lineHeight: 1.65,
        mb: 2,
        whiteSpace: 'pre-wrap',
      }}>
        {post.content}
      </Typography>

      {/* Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box onClick={handleLike} sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
          color: liked ? '#f472b6' : '#7fa8c8',
          '&:hover': { color: '#f472b6' },
          transition: 'color 0.2s',
        }}>
          {liked ? <Favorite sx={{ fontSize: 18 }} /> : <FavoriteBorder sx={{ fontSize: 18 }} />}
          <Typography sx={{ fontSize: '0.8rem' }}>{likeCount}</Typography>
        </Box>

        <Box onClick={() => setShowReplies(s => !s)} sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
          color: showReplies ? '#22d3ee' : '#7fa8c8',
          '&:hover': { color: '#22d3ee' },
          transition: 'color 0.2s',
        }}>
          <ChatBubbleOutlineOutlined sx={{ fontSize: 17 }} />
          <Typography sx={{ fontSize: '0.8rem' }}>{replies.length}</Typography>
        </Box>
      </Box>

      {/* Replies */}
      <Collapse in={showReplies}>
        <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid rgba(34,211,238,0.2)' }}>
          {replies.length === 0 && (
            <Typography sx={{ fontSize: '0.8rem', color: '#7fa8c8', mb: 1 }}>
              No replies yet. Be the first!
            </Typography>
          )}
          {replies.map((reply, i) => {
            const ru = reply.is_anonymous ? 'Anonymous' : (reply.author ?? 'Anonymous');
            return (
              <Box key={i} sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Avatar sx={{
                    width: 24, height: 24,
                    background: `${avatarColor(ru)}22`,
                    color: avatarColor(ru),
                    fontSize: '0.6rem',
                    fontWeight: 700,
                  }}>
                    {getInitials(ru)}
                  </Avatar>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#e8f4f8' }}>
                    {ru}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: '#7fa8c8' }}>
                    {timeAgo(reply.created_at ?? reply.timestamp)}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.85rem', color: '#d4e8f0', pl: 4, lineHeight: 1.5 }}>
                  {reply.content}
                </Typography>
              </Box>
            );
          })}
          <ReplyBox postId={post.id} onReplied={onRefresh} />
        </Box>
      </Collapse>
    </Box>
  );
}

// ── NewPostModal ──────────────────────────────────────────────────────────────

function NewPostModal({ onClose, onPosted, initialContent = '' }) {
  const [content, setContent] = useState(initialContent);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const maxChars = 500;

  async function submit() {
    if (!content.trim()) return;
    setLoading(true); setError('');
    try {
      await createPost({
        content: content.trim(),
        category: 'general',
        is_anonymous: true,
      });
      onPosted();
      onClose();
    } catch {
      setError('Could not post. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{
      position: 'fixed', inset: 0,
      background: 'rgba(5,12,25,0.85)',
      backdropFilter: 'blur(8px)',
      zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      px: 2,
    }}>
      <Box sx={{
        width: '100%', maxWidth: 520,
        background: 'linear-gradient(135deg, #0d1f3c, #0a2040)',
        border: '1px solid rgba(74,222,128,0.25)',
        borderRadius: 4,
        p: { xs: 2.5, sm: 3.5 },
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#e8f4f8' }}>
            Share with the Community
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: '#7fa8c8' }}>
            <Close />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{
            mb: 2, background: 'rgba(211,47,47,0.12)',
            color: '#ef9a9a', border: '1px solid rgba(211,47,47,0.3)',
            fontSize: '0.8rem',
          }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth multiline rows={5}
          placeholder="What's on your mind? Share a thought, encouragement, or experience…"
          value={content}
          onChange={e => setContent(e.target.value.slice(0, maxChars))}
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': {
              background: 'rgba(255,255,255,0.04)',
              color: '#e8f4f8',
              fontSize: '0.95rem',
              borderRadius: 2,
              '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
              '&:hover fieldset': { borderColor: 'rgba(74,222,128,0.3)' },
              '&.Mui-focused fieldset': { borderColor: '#4ade80' },
            },
            '& .MuiInputBase-input::placeholder': { color: '#7fa8c8' },
          }}
        />

        <Typography sx={{
          fontSize: '0.72rem',
          color: content.length > maxChars * 0.9 ? '#fbbf24' : '#7fa8c8',
          textAlign: 'right', mb: 2,
        }}>
          {content.length}/{maxChars}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button onClick={onClose} sx={{ color: '#7fa8c8', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!content.trim() || loading}
            sx={{
              background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
              color: '#0a1628',
              fontWeight: 700,
              textTransform: 'none',
              px: 3, borderRadius: 2,
              '&:hover': { opacity: 0.85 },
              '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: '#3a5a7a' },
            }}
          >
            {loading ? <CircularProgress size={18} sx={{ color: '#0a1628' }} /> : 'Post'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Community() {
  const navigate = useNavigate();

  const [posts,       setPosts]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [showModal,   setShowModal]   = useState(false);
  const [promptOpen,  setPromptOpen]  = useState(false);
  const [toast,       setToast]       = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    loadPosts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadPosts() {
    try {
      setLoading(true); setError('');
      const res  = await getPosts();
      const list = Array.isArray(res.data)
        ? res.data
        : res.data?.posts ?? res.data?.data ?? [];
      setPosts([...list].sort((a, b) =>
        new Date(b.created_at ?? 0) - new Date(a.created_at ?? 0)
      ));
    } catch {
      setError('Could not load posts. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  function handlePosted() {
    setToast('Post shared! 🎉');
    loadPosts();
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a1628 0%, #0d1f3c 50%, #0a2040 100%)',
      px: { xs: 2, sm: 4 },
      py: 4,
      color: '#e8f4f8',
    }}>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography sx={{
            fontSize: { xs: '1.8rem', sm: '2.2rem' },
            fontWeight: 800,
            background: 'linear-gradient(90deg, #4ade80, #22d3ee)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.5px',
          }}>
            Community
          </Typography>
          <Typography sx={{ color: '#7fa8c8', fontSize: '0.9rem', mt: 0.5 }}>
            You are not alone 🤝
          </Typography>
        </Box>
        <Button
          onClick={() => setShowModal(true)}
          startIcon={<Add />}
          sx={{
            background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
            color: '#0a1628',
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5, py: 1,
            flexShrink: 0,
            '&:hover': { opacity: 0.85 },
          }}
        >
          Post
        </Button>
      </Box>

      {/* Daily Prompt Card */}
      <Box
        onClick={() => setPromptOpen(true)}
        sx={{
          background: 'linear-gradient(135deg, rgba(74,222,128,0.1), rgba(34,211,238,0.08))',
          border: '1px solid rgba(74,222,128,0.25)',
          borderRadius: 3, p: 2.5, mb: 3, cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': { border: '1px solid rgba(74,222,128,0.5)', transform: 'translateY(-1px)' },
        }}
      >
        <Typography sx={{ color: '#4ade80', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.1em', mb: 0.5 }}>
          TODAY'S PROMPT ✨
        </Typography>
        <Typography sx={{ color: '#e8f4f8', fontSize: '0.95rem', lineHeight: 1.5, mb: 1 }}>
          {getDailyPrompt()}
        </Typography>
        <Typography sx={{ color: '#22d3ee', fontSize: '0.78rem', fontWeight: 600 }}>
          Tap to respond →
        </Typography>
      </Box>

      {/* Stats bar */}
      {!loading && posts.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <People sx={{ fontSize: 18, color: '#4ade80' }} />
          <Typography sx={{ fontSize: '0.85rem', color: '#7fa8c8' }}>
            <span style={{ color: '#4ade80', fontWeight: 700 }}>{posts.length}</span> posts in the community
          </Typography>
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#4ade80' }} />
        </Box>
      )}

      {/* Error */}
      {error && !loading && (
        <Alert severity="error" sx={{
          mb: 3,
          background: 'rgba(211,47,47,0.12)',
          color: '#ef9a9a',
          border: '1px solid rgba(211,47,47,0.3)',
        }}>
          {error}
        </Alert>
      )}

      {/* Empty state */}
      {!loading && !error && posts.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <People sx={{ fontSize: 64, color: '#3a5a7a', mb: 2 }} />
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#7fa8c8', mb: 1 }}>
            No posts yet
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#3a5a7a', mb: 3 }}>
            Be the first to share something with the community!
          </Typography>
          <Button
            onClick={() => setShowModal(true)}
            startIcon={<Add />}
            sx={{
              background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
              color: '#0a1628', fontWeight: 700,
              textTransform: 'none', borderRadius: 2, px: 3,
            }}
          >
            Write the first post
          </Button>
        </Box>
      )}

      {/* Posts feed */}
      {!loading && posts.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 680, mx: 'auto' }}>
          {posts.map(post => (
            <PostCard key={post.id} post={post} onRefresh={loadPosts} />
          ))}
        </Box>
      )}

      {/* New Post Modal */}
      {showModal && (
        <NewPostModal
          onClose={() => setShowModal(false)}
          onPosted={handlePosted}
        />
      )}

      {/* Prompt Modal — pre-fills with today's prompt */}
      {promptOpen && (
        <NewPostModal
          onClose={() => setPromptOpen(false)}
          onPosted={handlePosted}
          initialContent={getDailyPrompt()}
        />
      )}

      {/* Toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            background: 'rgba(74,222,128,0.15)',
            color: '#4ade80',
            border: '1px solid rgba(74,222,128,0.3)',
            borderRadius: 2,
          },
        }}
      />
    </Box>
  );
}